import {useMemo} from 'react';

import {RunHistoryRow} from '../../../types/run';
import {getMediaMetadata} from '../../../util/media';
import {RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {StepMeta, StepMetaRun} from '../types';
import {
  calculateStepBounds,
  resolveSliderStepCollisions,
  SliderStepMapping,
} from '../utils/stepUtils';

export function useStepMeta(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  mediaSliderKey: string,
  snapToExistingStep: boolean,
  runStepsMap: Map<string, RunHistoryRow[]>,
  mediaKeys?: string[]
): StepMeta {
  return useMemo(() => {
    // Early return if no runs with media
    if (runsWithMedia.length === 0) {
      return {
        runs: [],
        min: 0,
        max: 0,
        steps: [],
        stepSet: new Set<number>(),
        sliderKey: mediaSliderKey,
        minSliderStep: 0,
        maxSliderStep: 0,
        sliderSteps: [],
        sliderStepSet: new Set<number>(),
        minIndex: 0,
        maxIndex: 0,
        indices: [],
        indexSet: new Set<number>(),
      };
    }

    let globalStepMin = Infinity;
    let globalStepMax = -Infinity;
    let globalIndexMin = Infinity;
    let globalIndexMax = -Infinity;
    const globalSteps = new Set<number>();
    const globalSliderSteps = new Set<number>();
    const globalIndices = new Set<number>();
    const globalStepsBySliderStep = new Map<number, number>();

    // Build run-level data while recording global stats
    const runs = runsWithMedia.map(runHistory => {
      const steps = runStepsMap.get(runHistory.name);

      if (!steps) {
        // Properly type the empty run case
        return {
          id: runHistory.name,
          min: 0,
          max: 0,
          steps: [] as number[],
          stepSet: new Set<number>(),
          minSliderStep: 0,
          maxSliderStep: 0,
          sliderSteps: [] as number[],
          sliderStepSet: new Set<number>(),
          sliderKey: mediaSliderKey,
          minIndex: 0,
          maxIndex: 0,
          indices: [] as number[],
          indexSet: new Set<number>(),
        };
      }

      // Create step to slider step mappings
      const mappings: SliderStepMapping[] = steps.map(
        (history: RunHistoryRow) => ({
          step: history._step,
          sliderStep: history[mediaSliderKey],
        })
      );

      // resolving collisions using the most recent step value
      const {uniqueSliderSteps, stepsBySliderStep, resolvedSteps} =
        resolveSliderStepCollisions(mappings, 'latest');

      const {localResolveMin, localResolvedMax} =
        calculateStepBounds(resolvedSteps);
      const {
        localResolveMin: localSliderResolveMin,
        localResolvedMax: localSliderResolvedMax,
      } = calculateStepBounds(uniqueSliderSteps);

      // Calculate index data for this run
      let runIndices: number[] = [];
      let runIndexMin = Infinity;
      let runIndexMax = -Infinity;

      if (mediaKeys && mediaKeys.length > 0 && runHistory.history) {
        const firstMediaKey = mediaKeys[0];
        const allIndices = new Set<number>();

        // Collect all indices from all steps in this run
        for (const step of resolvedSteps) {
          try {
            const metadata = getMediaMetadata(
              runHistory.history,
              firstMediaKey,
              step
            );
            if (metadata) {
              // For single images, if there's no count property, assume count = 1
              const count = metadata.count ?? 1;
              const mediaGrouping = metadata.grouping || 1;
              const mediaCount = count / mediaGrouping;

              // Generate indices from 0 to mediaCount - 1
              for (let i = 0; i < mediaCount; i++) {
                allIndices.add(i);
              }
            }
          } catch (error) {
            // If there's an error getting metadata, continue without indices
            console.warn(
              'Error getting media metadata for step',
              step,
              'in run',
              runHistory.name,
              error
            );
          }
        }

        runIndices = Array.from(allIndices).sort((a, b) => a - b);
        if (runIndices.length > 0) {
          runIndexMin = Math.min(...runIndices);
          runIndexMax = Math.max(...runIndices);
        }
      }

      const runStats: StepMetaRun = {
        id: runHistory.name,
        min: localResolveMin,
        max: localResolvedMax,
        steps: resolvedSteps,
        stepSet: new Set(resolvedSteps),
        minSliderStep: localSliderResolveMin,
        maxSliderStep: localSliderResolvedMax,
        sliderSteps: uniqueSliderSteps,
        sliderStepSet: new Set(uniqueSliderSteps),
        sliderKey: mediaSliderKey,
        sliderStepMap: stepsBySliderStep,
        minIndex: runIndexMin === Infinity ? 0 : runIndexMin,
        maxIndex: runIndexMax === -Infinity ? 0 : runIndexMax,
        indices: runIndices,
        indexSet: new Set(runIndices),
      };

      // Update global stats
      globalStepMin = Math.min(globalStepMin, runStats.min);
      globalStepMax = Math.max(globalStepMax, runStats.max);
      globalIndexMin = Math.min(globalIndexMin, runStats.minIndex);
      globalIndexMax = Math.max(globalIndexMax, runStats.maxIndex);
      resolvedSteps.forEach(s => globalSteps.add(s));
      uniqueSliderSteps.forEach(s => globalSliderSteps.add(s));
      runIndices.forEach(i => globalIndices.add(i));

      // Update global step mapping
      stepsBySliderStep.forEach((step, sliderStep) => {
        if (
          !globalStepsBySliderStep.has(sliderStep) ||
          globalStepsBySliderStep.get(sliderStep)! < step
        ) {
          globalStepsBySliderStep.set(sliderStep, step);
        }
      });

      return runStats;
    });

    // Apply snapToExistingStep logic
    const minStep =
      !snapToExistingStep || globalStepMin === Infinity ? 0 : globalStepMin;
    const maxStep = globalStepMax === -Infinity ? 0 : globalStepMax;

    // Get ordered list of slider steps and their corresponding step values
    const allSliderSteps = Array.from(globalSliderSteps).sort((a, b) => a - b);
    const allSteps = Array.from(globalSteps).sort((a, b) => a - b);
    const allIndices = Array.from(globalIndices).sort((a, b) => a - b);

    const result = {
      runs,
      min: minStep,
      max: maxStep,
      steps: allSteps,
      stepSet: new Set(allSteps),
      sliderKey: mediaSliderKey,
      minSliderStep: allSliderSteps[0] ?? 0,
      maxSliderStep: allSliderSteps[allSliderSteps.length - 1] ?? 0,
      sliderSteps: allSliderSteps,
      sliderStepSet: new Set(allSliderSteps),
      minIndex: globalIndexMin === Infinity ? 0 : globalIndexMin,
      maxIndex: globalIndexMax === -Infinity ? 0 : globalIndexMax,
      indices: allIndices,
      indexSet: new Set(allIndices),
    };

    return result;
  }, [
    runsWithMedia,
    mediaSliderKey,
    snapToExistingStep,
    runStepsMap,
    mediaKeys,
  ]);
}
